import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';
import { getManagersForFactory } from '../../../data/internalDirectoryData';
import MIDataPanel from '../components/MIDataPanel';
import { fetchFactoryCardById, fetchLiveRatesForMill } from '../data';
import { generateFactoryRatePDF } from './generatePDF';
import NearbyMillsDistanceList from '../../market-intelligence/NearbyMillsDistanceList';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Shareable single-page view for one mill — reached via the Share/PDF icon on the FactoryCard.
// Deliberately lightweight: no RoleBasedHeader/filters, just the card's identity + full Market
// Intelligence + current live rate. "Download PDF" renders a real branded PDF via jsPDF
// (generatePDF.js), reusing the same header/watermark/footer drawing functions as the rest of
// SugarHathi's exports (market-intelligence/shareExport.js) instead of relying on window.print(),
// which produced an unbranded plain webpage printout.
const FactoryShareView = () => {
  const { millId } = useParams();
  const navigate = useNavigate();
  const miPanelRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const [managers, setManagers] = useState([]);
  const [liveRates, setLiveRates] = useState([]);
  const [liveRatesError, setLiveRatesError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { card: fetchedCard, error: cardError } = await fetchFactoryCardById(millId);
      if (cancelled) return;
      if (cardError) {
        setError(cardError);
        setLoading(false);
        return;
      }
      setCard(fetchedCard);

      const [mgrRes, ratesRes] = await Promise.all([
        fetchedCard?.directoryFactoryId ? getManagersForFactory(fetchedCard?.directoryFactoryId) : Promise.resolve({ data: [] }),
        fetchLiveRatesForMill(millId),
      ]);
      if (cancelled) return;
      setManagers(mgrRes?.data || []);
      setLiveRates(ratesRes?.rates || []);
      setLiveRatesError(ratesRes?.error || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [millId]);

  const handleDownloadPdf = async () => {
    if (!card) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await generateFactoryRatePDF({ card, managers, liveRates, chartRef: miPanelRef, action: 'download' });
    } catch (e) {
      setDownloadError(e?.message || 'Could not generate PDF. Please try again.');
    }
    setDownloading(false);
  };

  return (
    <>
      <Helmet>
        <title>{card?.name ? `${card?.name} – Rate Snapshot` : 'Mill Rate Snapshot'} – SugarHathi</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="min-h-screen" style={{ background: '#f8f1e9', fontFamily: "'DM Sans', sans-serif" }}>
        <div className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: '#e8ddd0' }}>
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <button
              onClick={() => navigate('/factory-rate-intelligence-center')}
              className="flex items-center gap-1.5 text-sm font-semibold"
              style={{ color: '#6b7280' }}
            >
              <Icon name="ArrowLeft" size={16} color="#6b7280" />
              Back
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={!card || downloading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)' }}
            >
              <Icon name="Download" size={15} color="#fff" />
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
          {downloadError && (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-2">
              <p className="text-xs" style={{ color: '#DC143C' }}>{downloadError}</p>
            </div>
          )}
        </div>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-8 h-8 rounded-full border-2 animate-spin mb-3" style={{ borderColor: '#fde68a', borderTopColor: '#f59e0b' }} />
              <p className="text-sm" style={{ color: '#6b7280' }}>Loading mill snapshot…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Icon name="AlertTriangle" size={28} color="#DC143C" />
              <p className="text-sm mt-3" style={{ color: '#6b7280' }}>{error}</p>
            </div>
          )}

          {!loading && !error && card && (
            <div className="bg-white rounded-2xl p-6" style={{ border: '1px solid #e8ddd0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              {/* Brand header */}
              <div className="flex items-center gap-3 mb-5 pb-4" style={{ borderBottom: '2px solid #fde68a' }}>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)' }}
                >
                  <Icon name="Zap" size={20} color="#ffffff" />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: '#1f2937' }}>SugarHathi Market Intelligence</p>
                  <p className="text-xs" style={{ color: '#6b7280' }}>Rate Snapshot · Generated {formatDate(new Date()?.toISOString())}</p>
                </div>
              </div>

              {/* ── Factory Identity ── */}
              <section className="mb-5">
                <h1 className="text-xl font-bold" style={{ color: '#1f2937' }}>{card?.name}</h1>
                <p className="text-sm mt-0.5" style={{ color: '#6b7280' }}>{card?.plantName}</p>

                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>{card?.state}</span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>{card?.district}</span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' }}>{card?.millCode}</span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>Address</dt>
                    <dd className="text-sm mt-0.5" style={{ color: '#1f2937' }}>{card?.address || '—'}</dd>
                  </div>
                  {card?.website && (
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>Website</dt>
                      <dd className="text-sm mt-0.5">
                        <a href={card?.website} target="_blank" rel="noopener noreferrer" style={{ color: '#d97706' }}>{card?.website}</a>
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>Phone</dt>
                    <dd className="text-sm mt-0.5" style={{ color: '#1f2937' }}>{card?.phone}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>Email</dt>
                    <dd className="text-sm mt-0.5" style={{ color: '#1f2937' }}>{card?.email}</dd>
                  </div>
                </dl>

                {managers?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#9ca3af' }}>Managers / Contacts</p>
                    <ul className="space-y-1.5">
                      {managers?.map((m) => (
                        <li key={m?.id} className="text-sm" style={{ color: '#1f2937' }}>
                          <span className="font-semibold">{m?.managerName}</span>
                          {m?.designation && <span style={{ color: '#6b7280' }}> · {m?.designation}</span>}
                          {(m?.mobile || m?.phone) && <span style={{ color: '#6b7280' }}> · {m?.mobile || m?.phone}</span>}
                          {m?.email && <span style={{ color: '#6b7280' }}> · {m?.email}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>

              {/* ── Market Intelligence ── */}
              <section className="mb-5" ref={miPanelRef}>
                <MIDataPanel data={card?.miData} />
              </section>

              {/* ── Current Live Rate ── */}
              <section>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#92400e' }}>
                  Current Live Rate
                </p>
                {liveRatesError && (
                  <p className="text-xs" style={{ color: '#DC143C' }}>{liveRatesError}</p>
                )}
                {!liveRatesError && liveRates?.length === 0 && (
                  <p className="text-xs" style={{ color: '#6b7280' }}>
                    No live rate currently on record for this mill.
                  </p>
                )}
                {!liveRatesError && liveRates?.length > 0 && (
                  <div className="space-y-2">
                    {liveRates?.map((r) => (
                      <div key={r?.id} className="rounded-lg p-3" style={{ background: '#f8f1e9', border: '1px solid #e8ddd0' }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-bold" style={{ color: '#1f2937' }}>{r?.grade}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#dcfce7', color: '#16a34a' }}>Live</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" style={{ color: '#6b7280' }}>
                          <div>Rate Excl GST<br /><span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(r?.rate_excl_gst)?.toLocaleString('en-IN')}</span></div>
                          <div>Rate Incl GST<br /><span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(r?.rate_incl_gst)?.toLocaleString('en-IN')}</span></div>
                          <div>Payment Date<br /><span className="font-semibold" style={{ color: '#1f2937' }}>{formatDate(r?.payment_date)}</span></div>
                          <div>Lifting Date<br /><span className="font-semibold" style={{ color: '#1f2937' }}>{formatDate(r?.lifting_date)}</span></div>
                        </div>
                        <p className="text-xs mt-1.5" style={{ color: '#6b7280' }}>
                          Available Qty: <span className="font-semibold" style={{ color: '#1f2937' }}>{r?.available_qty} MT</span>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ── Other Mills in This District ── */}
              <section className="mt-5">
                <NearbyMillsDistanceList millId={millId} millName={card?.name} accentColor="#f59e0b" titleColor="#92400e" />
              </section>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default FactoryShareView;
